import { EditMaterialInputType } from '@shelflix/schemas/material/edit';
import { checkMaterialEditInput } from '@shelflix/schemas/material/edit';
import { ELedPanelOutputResolution, ELedPanelTarget } from '@shelflix/schemas/material/enums';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { UseFormReturn } from 'react-hook-form';
import { LedPanelVariantsStatus } from './LedPanelVariantsStatus';

interface LedPanelSectionProps {
  materialUuid: string;
  form: UseFormReturn<
    EditMaterialInputType & {
      cnpj: {
        uuid: string;
        cnpj: string;
        name: string;
      };
      materialTypeUuid: {
        uuid: string;
        name: string;
        brand: {
          name: string;
        };
      };
    }
  >;
}

const sizeFields: { name: keyof EditMaterialInputType; label: string; placeholder: string }[] = [
  { name: 'ledPanelWidthPx', label: 'Largura (px)', placeholder: 'Ex: 512' },
  { name: 'ledPanelHeightPx', label: 'Altura (px)', placeholder: 'Ex: 128' },
];

const tvboxOffsetFields: { name: keyof EditMaterialInputType; label: string; placeholder: string }[] = [
  { name: 'ledPanelOffsetXPx', label: 'Deslocamento X (px)', placeholder: 'Ex: 0' },
  { name: 'ledPanelOffsetYPx', label: 'Deslocamento Y (px)', placeholder: 'Ex: 0' },
];

export function LedPanelSection({ materialUuid, form }: LedPanelSectionProps) {
  const ledPanelTarget = form.watch('ledPanelTarget');
  const isEsp32Target = ledPanelTarget === ELedPanelTarget.ESP32_HUB75;

  return (
    <Card className="rounded-[10px]">
      <CardHeader className="pb-0">
        <CardTitle className="text-primary text-2xl leading-5 font-semibold">Painel LED</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-4 gap-4">
        <FormField
          control={form.control}
          name="ledPanelTarget"
          rules={{ validate: (value) => checkMaterialEditInput('ledPanelTarget', value) }}
          render={({ field }) => (
            <FormItem className="col-span-2 flex w-full flex-col gap-[3px]">
              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Alvo</FormLabel>
              <Select
                value={field.value ?? ELedPanelTarget.TVBOX_HDMI}
                onValueChange={(value) => {
                  field.onChange(value);
                  if (value === ELedPanelTarget.ESP32_HUB75) {
                    form.setValue('ledPanelOffsetXPx', undefined);
                    form.setValue('ledPanelOffsetYPx', undefined);
                    form.setValue('ledPanelOutputResolution', undefined);
                  } else {
                    form.setValue('ledPanelFps', undefined);
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger className="h-[36px] w-full">
                    <SelectValue placeholder="Selecione o alvo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={ELedPanelTarget.TVBOX_HDMI}>TV box (HDMI)</SelectItem>
                  <SelectItem value={ELedPanelTarget.ESP32_HUB75}>Esp32-HUB75-Matrix</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {sizeFields.map(({ name, label, placeholder }) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            rules={{ validate: (value) => checkMaterialEditInput(name, value) }}
            render={({ field }) => (
              <FormItem className="flex w-full flex-col gap-[3px]">
                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">{label}</FormLabel>
                <FormControl>
                  <Input
                    className="h-[36px]"
                    type="number"
                    placeholder={placeholder}
                    {...field}
                    value={(field.value as number | undefined) ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}

        {isEsp32Target ? (
          <FormField
            control={form.control}
            name="ledPanelFps"
            rules={{ validate: (value) => checkMaterialEditInput('ledPanelFps', value) }}
            render={({ field }) => (
              <FormItem className="flex w-full flex-col gap-[3px]">
                <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">FPS</FormLabel>
                <FormControl>
                  <Input
                    className="h-[36px]"
                    type="number"
                    placeholder="Ex: 30"
                    {...field}
                    value={(field.value as number | undefined) ?? ''}
                    onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <>
            {tvboxOffsetFields.map(({ name, label, placeholder }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                rules={{ validate: (value) => checkMaterialEditInput(name, value) }}
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col gap-[3px]">
                    <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">{label}</FormLabel>
                    <FormControl>
                      <Input
                        className="h-[36px]"
                        type="number"
                        placeholder={placeholder}
                        {...field}
                        value={(field.value as number | undefined) ?? ''}
                        onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
            <FormField
              control={form.control}
              name="ledPanelOutputResolution"
              rules={{ validate: (value) => checkMaterialEditInput('ledPanelOutputResolution', value) }}
              render={({ field }) => (
                <FormItem className="flex w-full flex-col gap-[3px]">
                  <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                    Resolução de saída da tvbox
                  </FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="h-[36px] w-full">
                        <SelectValue placeholder="Selecione a resolução" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={ELedPanelOutputResolution.HD_1280x720}>HD (1280x720)</SelectItem>
                      <SelectItem value={ELedPanelOutputResolution.FULL_HD_1920x1080}>Full HD (1920x1080)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <div className="col-span-4 border-t border-[var(--border)] pt-4">
          <LedPanelVariantsStatus materialUuid={materialUuid} />
        </div>
      </CardContent>
    </Card>
  );
}
